import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  Plane,
  Clock,
  Shield,
  Globe,
  Package,
  Thermometer,
  AlertTriangle,
  Gem,
  Pill,
  Cpu,
  CheckCircle,
  ArrowRight,
  Phone,
  Mail,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Star,
  Zap,
  Target,
  Award,
  Send,
  MapPin
} from 'lucide-react';

// Animated counter component
const AnimatedCounter = ({ end, suffix = '', duration = 2000, isVisible }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, isVisible]);

  return <span>{count}{suffix}</span>;
};

// FAQ Accordion Item
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-gray-200 dark:border-gray-700">
    <button
      onClick={onClick}
      className="w-full py-5 flex items-center justify-between text-left hover:text-[#3ECDA3] transition-colors"
    >
      <span className="font-medium text-gray-900 dark:text-white pr-8">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-[#3ECDA3] flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}>
      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{answer}</p>
    </div>
  </div>
);

const AirTransportPage = () => {
  const { language } = useLanguage();
  const [openFAQ, setOpenFAQ] = useState(0);
  const [statsVisible, setStatsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    origin: '',
    destination: '',
    cargoType: '',
    urgency: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const statsRef = useRef(null);
  const formRef = useRef(null);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    pt: {
      hero: {
        badge: 'TRANSPORTE AÉREO INTERNACIONAL',
        title: 'Velocidade e Segurança',
        titleHighlight: 'para sua Carga',
        subtitle: 'Soluções aéreas personalizadas com as principais companhias do mundo. Sua carga chega mais rápido, com total rastreabilidade e segurança.',
        cta: 'SOLICITAR COTAÇÃO',
        ctaSecondary: 'FALAR COM ESPECIALISTA'
      },
      benefits: {
        title: 'Por que escolher o',
        titleHighlight: 'Transporte Aéreo?',
        items: [
          { icon: Zap, title: 'Rapidez', desc: 'É o modal mais rápido do comércio internacional, ideal para reduzir lead time, atender prazos urgentes e manter a continuidade da cadeia produtiva.' },
          { icon: Target, title: 'Maior previsibilidade', desc: 'Voos regulares e menor exposição a atrasos climáticos ou operacionais garantem mais confiabilidade nos prazos de entrega.' },
          { icon: Shield, title: 'Segurança da carga', desc: 'Menor tempo de trânsito reduz riscos de avarias, extravios e roubos, sendo indicado para cargas de alto valor agregado.' },
          { icon: Package, title: 'Ideal para cargas sensíveis', desc: 'Perfeito para mercadorias perecíveis, farmacêuticas, eletrônicos, amostras, peças urgentes e produtos com prazo de validade curto.' },
          { icon: Globe, title: 'Alcance global', desc: 'Conecta rapidamente os principais mercados do mundo, com ampla malha aérea internacional.' },
          { icon: Award, title: 'Parceria estratégica', desc: 'A Well Cargo é parceira das principais companhias aéreas do mercado, oferecendo soluções ágeis e seguras para importações e exportações.' }
        ]
      },
      cargoTypes: {
        title: 'Tipos de Carga que',
        titleHighlight: 'Transportamos',
        items: [
          { icon: AlertTriangle, name: 'Cargas Perigosas', desc: 'DGR certificado para mercadorias especiais' },
          { icon: Thermometer, name: 'Perecíveis', desc: 'Controle de temperatura durante todo trajeto' },
          { icon: Gem, name: 'Alto Valor', desc: 'Segurança reforçada para cargas valiosas' },
          { icon: Pill, name: 'Farmacêuticos', desc: 'GDP compliant para medicamentos' },
          { icon: Cpu, name: 'Eletrônicos', desc: 'Embalagem especial e manuseio cuidadoso' },
          { icon: Package, name: 'Carga Geral', desc: 'Qualquer tipo de mercadoria padrão' }
        ]
      },
      stats: {
        title: 'Nossa Expertise em',
        titleHighlight: 'Números',
        items: [
          { value: 24, suffix: 't', label: 'Toneladas/ano' },
          { value: 150, suffix: '+', label: 'Aeroportos' },
          { value: 98, suffix: '%', label: 'Pontualidade' },
          { value: 48, suffix: 'h', label: 'Tempo médio' }
        ]
      },
      process: {
        title: 'Como Funciona',
        steps: [
          { num: '01', title: 'Cotação', desc: 'Receba uma proposta personalizada em até 2 horas' },
          { num: '02', title: 'Coleta', desc: 'Retirada da carga em qualquer local do Brasil' },
          { num: '03', title: 'Documentação', desc: 'Cuidamos de toda burocracia e desembaraço' },
          { num: '04', title: 'Embarque', desc: 'Acompanhamento do status do voo' },
          { num: '05', title: 'Entrega', desc: 'Sua carga chega ao destino com segurança' }
        ]
      },
      testimonial: {
        quote: 'A Well Cargo transformou nossa logística de exportação. Cargas que levavam semanas agora chegam em dias, com total visibilidade.',
        author: 'Roberto Mendes',
        role: 'Diretor de Operações',
        company: 'TechExport Brasil'
      },
      faq: {
        title: 'Perguntas',
        titleHighlight: 'Frequentes',
        items: [
          { q: 'Quando o transporte aéreo é a melhor opção?', a: 'Quando há urgência na entrega, alto valor agregado da mercadoria, cargas sensíveis ou necessidade de previsibilidade nos prazos.' },
          { q: 'Quais tipos de carga podem ser transportados por via aérea?', a: 'Cargas gerais, perecíveis, farmacêuticos, eletrônicos, amostras, peças urgentes, cargas perigosas (com restrições), animais vivos e produtos de alto valor.' },
          { q: 'Existe limite de peso ou volume no transporte aéreo?', a: 'Sim. Os limites variam conforme o tipo de aeronave, companhia aérea e rota. Além disso, o frete é calculado pelo peso taxável.' },
          { q: 'O que é peso taxável?', a: 'É o maior valor entre o peso bruto real e o peso cubado da carga. No aéreo, normalmente utiliza-se a fórmula: comprimento x largura x altura (cm) ÷ 6.000.' },
          { q: 'O transporte aéreo aceita carga perigosa?', a: 'Sim, desde que a carga esteja classificada, embalada, documentada e rotulada conforme as normas internacionais. Nem todas as companhias aceitam todos os tipos de mercadorias perigosas.' },
          { q: 'Quais documentos são necessários no transporte aéreo internacional?', a: 'Normalmente: invoice, packing list, conhecimento aéreo (HAWB e MAWB), documentos regulatórios específicos e, em alguns casos, licenças ou certificados.' },
          { q: 'Qual é o prazo médio de trânsito no modal aéreo?', a: 'Pode variar de 1 a 15 dias, dependendo da origem, destino, conexões e liberação aduaneira.' },
          { q: 'O transporte aéreo é sempre mais caro?', a: 'O custo é mais elevado que outros modais, porém pode ser compensado pela redução de estoque, menor risco, agilidade e cumprimento de prazos críticos.' },
          { q: 'Posso consolidar cargas no transporte aéreo?', a: 'Sim. A consolidação aérea é comum e permite otimizar custos ao agrupar cargas de diferentes embarcadores em um único embarque.' },
          { q: 'É possível acompanhar a carga durante o transporte?', a: 'Sim. O modal aéreo oferece alto nível de rastreabilidade, com atualizações frequentes de status do embarque.' },
        ]
      },
      cta: {
        title: 'Pronto para Acelerar',
        titleHighlight: 'suas Exportações?',
        subtitle: 'Preencha o formulário e receba uma cotação personalizada em até 24 horas.',
        badge: 'RESPOSTA RÁPIDA'
      },
      form: {
        name: 'Nome completo',
        email: 'E-mail',
        phone: 'WhatsApp',
        company: 'Empresa',
        origin: 'Origem da carga',
        destination: 'Destino',
        cargoType: 'Tipo de carga',
        urgency: 'Urgência',
        message: 'Detalhes adicionais (opcional)',
        submit: 'SOLICITAR COTAÇÃO GRÁTIS',
        success: 'Recebemos sua solicitação! Nossa equipe entrará em contato em breve.',
        urgencyOptions: ['Normal (5-7 dias)', 'Urgente (3-5 dias)', 'Express (24-48h)'],
        cargoOptions: ['Carga Geral', 'Perecível', 'Perigosa (DGR)', 'Alto Valor', 'Farmacêutica', 'Eletrônicos', 'Outros']
      },
      contact: {
        title: 'Prefere falar diretamente?',
        phone: '+55 27 3534-2660',
        whatsapp: '+55 31 99592-5853',
        email: 'aereo@wellcargo.com.br'
      }
    },
    en: {
      hero: {
        badge: 'INTERNATIONAL AIR TRANSPORT',
        title: 'Speed and Security',
        titleHighlight: 'for your Cargo',
        subtitle: 'Customized air solutions with the world\'s leading airlines. Your cargo arrives faster, with full traceability and security.',
        cta: 'REQUEST QUOTE',
        ctaSecondary: 'TALK TO SPECIALIST'
      },
      benefits: {
        title: 'Why choose',
        titleHighlight: 'Air Transport?',
        items: [
          { icon: Zap, title: 'Speed', desc: 'It is the fastest mode in international trade, ideal for reducing lead time, meeting urgent deadlines and maintaining supply chain continuity.' },
          { icon: Target, title: 'Greater predictability', desc: 'Regular flights and less exposure to weather or operational delays ensure more reliability in delivery times.' },
          { icon: Shield, title: 'Cargo security', desc: 'Shorter transit time reduces risks of damage, loss and theft, being indicated for high added value cargo.' },
          { icon: Package, title: 'Ideal for sensitive cargo', desc: 'Perfect for perishable goods, pharmaceuticals, electronics, samples, urgent parts and products with short shelf life.' },
          { icon: Globe, title: 'Global reach', desc: 'Quickly connects the main markets in the world, with extensive international air network.' },
          { icon: Award, title: 'Strategic partnership', desc: 'Well Cargo is a partner of the main airlines in the market, offering agile and secure solutions for imports and exports.' }
        ]
      },
      cargoTypes: {
        title: 'Types of Cargo we',
        titleHighlight: 'Transport',
        items: [
          { icon: AlertTriangle, name: 'Hazardous Cargo', desc: 'DGR certified for special goods' },
          { icon: Thermometer, name: 'Perishables', desc: 'Temperature control throughout journey' },
          { icon: Gem, name: 'High Value', desc: 'Enhanced security for valuable cargo' },
          { icon: Pill, name: 'Pharmaceuticals', desc: 'GDP compliant for medications' },
          { icon: Cpu, name: 'Electronics', desc: 'Special packaging and careful handling' },
          { icon: Package, name: 'General Cargo', desc: 'Any type of standard merchandise' }
        ]
      },
      stats: {
        title: 'Our Expertise in',
        titleHighlight: 'Numbers',
        items: [
          { value: 24, suffix: 't', label: 'Tons/year' },
          { value: 150, suffix: '+', label: 'Airports' },
          { value: 98, suffix: '%', label: 'Punctuality' },
          { value: 48, suffix: 'h', label: 'Average time' }
        ]
      },
      process: {
        title: 'How it Works',
        steps: [
          { num: '01', title: 'Quote', desc: 'Receive a personalized proposal within 2 hours' },
          { num: '02', title: 'Pickup', desc: 'Cargo collection anywhere in Brazil' },
          { num: '03', title: 'Documentation', desc: 'We handle all paperwork and clearance' },
          { num: '04', title: 'Shipping', desc: 'Real-time flight monitoring' },
          { num: '05', title: 'Delivery', desc: 'Your cargo arrives safely at destination' }
        ]
      },
      testimonial: {
        quote: 'Well Cargo transformed our export logistics. Shipments that took weeks now arrive in days, with full visibility.',
        author: 'Roberto Mendes',
        role: 'Operations Director',
        company: 'TechExport Brasil'
      },
      faq: {
        title: 'Frequently Asked',
        titleHighlight: 'Questions',
        items: [
          { q: 'When is air transport the best option?', a: 'When there is urgency in delivery, high added value of the goods, sensitive cargo or need for predictability in deadlines.' },
          { q: 'What types of cargo can be transported by air?', a: 'General cargo, perishables, pharmaceuticals, electronics, samples, urgent parts, dangerous goods (with restrictions), live animals and high-value products.' },
          { q: 'Are there weight or volume limits in air transport?', a: 'Yes. Limits vary according to aircraft type, airline and route. Additionally, freight is calculated by chargeable weight.' },
          { q: 'What is chargeable weight?', a: 'It is the greater value between the actual gross weight and the volumetric weight of the cargo. In air freight, the formula typically used is: length x width x height (cm) ÷ 6,000.' },
          { q: 'Does air transport accept dangerous cargo?', a: 'Yes, as long as the cargo is classified, packaged, documented and labeled according to international standards. Not all airlines accept all types of dangerous goods.' },
          { q: 'What documents are required for international air transport?', a: 'Usually: invoice, packing list, air waybill (HAWB and MAWB), specific regulatory documents and, in some cases, licenses or certificates.' },
          { q: 'What is the average transit time for air freight?', a: 'It can vary from 1 to 15 days, depending on origin, destination, connections and customs clearance.' },
          { q: 'Is air transport always more expensive?', a: 'The cost is higher than other modes, but it can be offset by reduced inventory, lower risk, agility and meeting critical deadlines.' },
          { q: 'Can I consolidate cargo in air transport?', a: 'Yes. Air consolidation is common and allows cost optimization by grouping cargo from different shippers into a single shipment.' },
          { q: 'Is it possible to track cargo during transport?', a: 'Yes. Air freight offers a high level of traceability, with frequent shipment status updates.' },
        ]
      },
      cta: {
        title: 'Ready to Accelerate',
        titleHighlight: 'your Exports?',
        subtitle: 'Fill out the form and receive a personalized quote within 24 hours.',
        badge: 'FAST RESPONSE'
      },
      form: {
        name: 'Full name',
        email: 'Email',
        phone: 'WhatsApp',
        company: 'Company',
        origin: 'Cargo origin',
        destination: 'Destination',
        cargoType: 'Cargo type',
        urgency: 'Urgency',
        message: 'Additional details (optional)',
        submit: 'REQUEST FREE QUOTE',
        success: 'We received your request! Our team will contact you soon.',
        urgencyOptions: ['Normal (5-7 days)', 'Urgent (3-5 days)', 'Express (24-48h)'],
        cargoOptions: ['General Cargo', 'Perishable', 'Hazardous (DGR)', 'High Value', 'Pharmaceutical', 'Electronics', 'Others']
      },
      contact: {
        title: 'Prefer to talk directly?',
        phone: '+55 27 3534-2660',
        whatsapp: '+55 31 99592-5853',
        email: 'aereo@wellcargo.com.br'
      }
    },
    es: {
      hero: {
        badge: 'TRANSPORTE AÉREO INTERNACIONAL',
        title: 'Velocidad y Seguridad',
        titleHighlight: 'para tu Carga',
        subtitle: 'Soluciones aéreas personalizadas con las principales aerolíneas del mundo. Tu carga llega más rápido, con total trazabilidad y seguridad.',
        cta: 'SOLICITAR COTIZACIÓN',
        ctaSecondary: 'HABLAR CON ESPECIALISTA'
      },
      benefits: {
        title: '¿Por qué elegir el',
        titleHighlight: 'Transporte Aéreo?',
        items: [
          { icon: Zap, title: 'Rapidez', desc: 'Es el modal más rápido del comercio internacional, ideal para reducir lead time, atender plazos urgentes y mantener la continuidad de la cadena productiva.' },
          { icon: Target, title: 'Mayor previsibilidad', desc: 'Vuelos regulares y menor exposición a retrasos climáticos u operacionales garantizan más confiabilidad en los plazos de entrega.' },
          { icon: Shield, title: 'Seguridad de la carga', desc: 'Menor tiempo de tránsito reduce riesgos de averías, extravíos y robos, siendo indicado para cargas de alto valor agregado.' },
          { icon: Package, title: 'Ideal para cargas sensibles', desc: 'Perfecto para mercancías perecederas, farmacéuticas, electrónicos, muestras, piezas urgentes y productos con plazo de validez corto.' },
          { icon: Globe, title: 'Alcance global', desc: 'Conecta rápidamente los principales mercados del mundo, con amplia malla aérea internacional.' },
          { icon: Award, title: 'Asociación estratégica', desc: 'Well Cargo es socio de las principales aerolíneas del mercado, ofreciendo soluciones ágiles y seguras para importaciones y exportaciones.' }
        ]
      },
      cargoTypes: {
        title: 'Tipos de Carga que',
        titleHighlight: 'Transportamos',
        items: [
          { icon: AlertTriangle, name: 'Cargas Peligrosas', desc: 'Certificado DGR para mercancías especiales' },
          { icon: Thermometer, name: 'Perecederos', desc: 'Control de temperatura durante el trayecto' },
          { icon: Gem, name: 'Alto Valor', desc: 'Seguridad reforzada para cargas valiosas' },
          { icon: Pill, name: 'Farmacéuticos', desc: 'GDP compliant para medicamentos' },
          { icon: Cpu, name: 'Electrónicos', desc: 'Embalaje especial y manejo cuidadoso' },
          { icon: Package, name: 'Carga General', desc: 'Cualquier tipo de mercancía estándar' }
        ]
      },
      stats: {
        title: 'Nuestra Experiencia en',
        titleHighlight: 'Números',
        items: [
          { value: 24, suffix: 't', label: 'Toneladas/año' },
          { value: 150, suffix: '+', label: 'Aeropuertos' },
          { value: 98, suffix: '%', label: 'Puntualidad' },
          { value: 48, suffix: 'h', label: 'Tiempo promedio' }
        ]
      },
      process: {
        title: 'Cómo Funciona',
        steps: [
          { num: '01', title: 'Cotización', desc: 'Recibe una propuesta personalizada en 2 horas' },
          { num: '02', title: 'Recolección', desc: 'Retiro de carga en cualquier lugar de Brasil' },
          { num: '03', title: 'Documentación', desc: 'Manejamos toda la burocracia y despacho' },
          { num: '04', title: 'Embarque', desc: 'Monitoreo en tiempo real del vuelo' },
          { num: '05', title: 'Entrega', desc: 'Tu carga llega al destino con seguridad' }
        ]
      },
      testimonial: {
        quote: 'Well Cargo transformó nuestra logística de exportación. Envíos que tardaban semanas ahora llegan en días, con total visibilidad.',
        author: 'Roberto Mendes',
        role: 'Director de Operaciones',
        company: 'TechExport Brasil'
      },
      faq: {
        title: 'Preguntas',
        titleHighlight: 'Frecuentes',
        items: [
          { q: '¿Cuándo es el transporte aéreo la mejor opción?', a: 'Cuando hay urgencia en la entrega, alto valor agregado de la mercancía, cargas sensibles o necesidad de previsibilidad en los plazos.' },
          { q: '¿Qué tipos de carga pueden transportarse por vía aérea?', a: 'Cargas generales, perecederos, farmacéuticos, electrónicos, muestras, piezas urgentes, cargas peligrosas (con restricciones), animales vivos y productos de alto valor.' },
          { q: '¿Existe límite de peso o volumen en el transporte aéreo?', a: 'Sí. Los límites varían según el tipo de aeronave, aerolínea y ruta. Además, el flete se calcula por el peso tasable.' },
          { q: '¿Qué es el peso tasable?', a: 'Es el mayor valor entre el peso bruto real y el peso volumétrico de la carga. En el aéreo, normalmente se utiliza la fórmula: largo x ancho x alto (cm) ÷ 6.000.' },
          { q: '¿El transporte aéreo acepta carga peligrosa?', a: 'Sí, siempre que la carga esté clasificada, embalada, documentada y etiquetada conforme a las normas internacionales. No todas las aerolíneas aceptan todos los tipos de mercancías peligrosas.' },
          { q: '¿Qué documentos son necesarios en el transporte aéreo internacional?', a: 'Normalmente: invoice, packing list, conocimiento aéreo (HAWB y MAWB), documentos regulatorios específicos y, en algunos casos, licencias o certificados.' },
          { q: '¿Cuál es el plazo medio de tránsito en el modal aéreo?', a: 'Puede variar de 1 a 15 días, dependiendo del origen, destino, conexiones y liberación aduanera.' },
          { q: '¿El transporte aéreo es siempre más caro?', a: 'El costo es más elevado que otros modales, pero puede compensarse por la reducción de inventario, menor riesgo, agilidad y cumplimiento de plazos críticos.' },
          { q: '¿Puedo consolidar cargas en el transporte aéreo?', a: 'Sí. La consolidación aérea es común y permite optimizar costos al agrupar cargas de diferentes embarcadores en un único embarque.' },
          { q: '¿Es posible acompañar la carga en tiempo real?', a: 'Sí. El modal aéreo ofrece alto nivel de trazabilidad, con actualizaciones frecuentes del estado del embarque.' },
        ]
      },
      cta: {
        title: '¿Listo para Acelerar',
        titleHighlight: 'tus Exportaciones?',
        subtitle: 'Completa el formulario y recibe una cotización personalizada en 24 horas.',
        badge: 'RESPUESTA RÁPIDA'
      },
      form: {
        name: 'Nombre completo',
        email: 'Correo electrónico',
        phone: 'WhatsApp',
        company: 'Empresa',
        origin: 'Origen de la carga',
        destination: 'Destino',
        cargoType: 'Tipo de carga',
        urgency: 'Urgencia',
        message: 'Detalles adicionales (opcional)',
        submit: 'SOLICITAR COTIZACIÓN GRATIS',
        success: '¡Recibimos tu solicitud! Nuestro equipo te contactará pronto.',
        urgencyOptions: ['Normal (5-7 días)', 'Urgente (3-5 días)', 'Express (24-48h)'],
        cargoOptions: ['Carga General', 'Perecedero', 'Peligrosa (DGR)', 'Alto Valor', 'Farmacéutica', 'Electrónicos', 'Otros']
      },
      contact: {
        title: '¿Prefieres hablar directamente?',
        phone: '+55 27 3534-2660',
        whatsapp: '+55 31 99592-5853',
        email: 'aereo@wellcargo.com.br'
      }
    }
  };

  const c = content[language];

  // Intersection observer for stats
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsVisible(true);
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      console.log('Lead form submitted:', formData);
      setIsSubmitting(false);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', company: '', origin: '', destination: '', cargoType: '', urgency: '', message: '' });
    }, 1500);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1920&q=80')" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>

        {/* Animated particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-[#3ECDA3]/40 rounded-full animate-float"
              style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, animationDelay: `${i * 0.3}s` }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#3ECDA3]/20 border border-[#3ECDA3]/40 rounded-full px-4 py-2 mb-6 animate-fade-in">
              <Plane className="w-4 h-4 text-[#3ECDA3]" />
              <span className="text-[#3ECDA3] text-sm font-medium">{c.hero.badge}</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              {c.hero.title}<br />
              <span className="text-[#3ECDA3] italic">{c.hero.titleHighlight}</span>
            </h1>

            <p className="text-gray-300 text-lg md:text-xl mb-8 leading-relaxed animate-fade-in" style={{ animationDelay: '0.4s' }}>
              {c.hero.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in" style={{ animationDelay: '0.6s' }}>
              <button
                onClick={scrollToForm}
                className="group bg-[#3ECDA3] hover:bg-[#35b892] text-white px-8 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-lg hover:shadow-[#3ECDA3]/30"
              >
                {c.hero.cta}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="https://wa.me/5531995925853"
                target="_blank"
                rel="noopener noreferrer"
                className="group border-2 border-white/30 hover:border-[#3ECDA3] text-white px-8 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 hover:bg-[#3ECDA3]/10"
              >
                <MessageCircle className="w-5 h-5" />
                {c.hero.ctaSecondary}
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <button onClick={scrollToForm} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white animate-bounce">
          <ChevronDown size={32} />
        </button>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-28 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              {c.benefits.title} <span className="text-[#3ECDA3] italic">{c.benefits.titleHighlight}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {c.benefits.items.map((item, index) => {
              const IconComp = item.icon;
              return (
                <div
                  key={index}
                  className="group bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 dark:border-gray-700"
                >
                  <div className="w-14 h-14 bg-[#3ECDA3]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#3ECDA3] transition-colors duration-300">
                    <IconComp className="w-7 h-7 text-[#3ECDA3] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{item.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cargo Types */}
      <section className="py-20 md:py-28 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              {c.cargoTypes.title} <span className="text-[#3ECDA3] italic">{c.cargoTypes.titleHighlight}</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {c.cargoTypes.items.map((item, index) => {
              const IconComp = item.icon;
              return (
                <div key={index} className="group text-center p-6 rounded-xl hover:bg-[#3ECDA3]/5 transition-colors">
                  <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4 group-hover:bg-[#3ECDA3]/20 transition-colors">
                    <IconComp className="w-8 h-8 text-[#3ECDA3]" />
                  </div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1">{item.name}</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-20 bg-[#1a6b5c] dark:bg-[#0f4a3f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {c.stats.title} <span className="italic text-[#3ECDA3]">{c.stats.titleHighlight}</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {c.stats.items.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} isVisible={statsVisible} />
                </div>
                <p className="text-[#3ECDA3] text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 fill-[#3ECDA3] text-[#3ECDA3]" />
            ))}
          </div>
          <blockquote className="text-2xl md:text-3xl text-gray-700 dark:text-gray-200 italic leading-relaxed mb-8">
            "{c.testimonial.quote}"
          </blockquote>
          <div>
            <p className="font-bold text-gray-900 dark:text-white">{c.testimonial.author}</p>
            <p className="text-gray-500 dark:text-gray-400">{c.testimonial.role}, {c.testimonial.company}</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white text-center mb-12">
            {c.faq.title} <span className="text-[#3ECDA3] italic">{c.faq.titleHighlight}</span>
          </h2>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm p-6 md:p-8">
            {c.faq.items.map((item, index) => (
              <FAQItem
                key={index}
                question={item.q}
                answer={item.a}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA & Form Section */}
      <section ref={formRef} className="py-20 md:py-28 bg-gradient-to-br from-[#1a6b5c] to-[#0f3d35] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#3ECDA3] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left - CTA Text */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
                <Zap className="w-4 h-4 text-[#3ECDA3]" />
                <span className="text-[#3ECDA3] text-sm font-medium">{c.cta.badge}</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                {c.cta.title}<br />
                <span className="text-[#3ECDA3] italic">{c.cta.titleHighlight}</span>
              </h2>
              <p className="text-gray-300 text-lg mb-8">{c.cta.subtitle}</p>

              {/* Contact alternatives */}
              <div className="space-y-4">
                <p className="text-white/70 text-sm">{c.contact.title}</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <a href={`tel:${c.contact.phone}`} className="flex items-center gap-2 text-white hover:text-[#3ECDA3] transition-colors">
                    <Phone className="w-5 h-5" />
                    <span>{c.contact.phone}</span>
                  </a>
                  <a href={`https://wa.me/${c.contact.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white hover:text-[#3ECDA3] transition-colors">
                    <MessageCircle className="w-5 h-5" />
                    <span>WhatsApp</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-[#3ECDA3] mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Recebemos!</h3>
                  <p className="text-gray-600 dark:text-gray-400">{c.form.success}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input
                      type="text" name="name" required placeholder={c.form.name} value={formData.name} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                    <input
                      type="email" name="email" required placeholder={c.form.email} value={formData.email} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input
                      type="tel" name="phone" required placeholder={c.form.phone} value={formData.phone} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                    <input
                      type="text" name="company" placeholder={c.form.company} value={formData.company} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input
                      type="text" name="origin" required placeholder={c.form.origin} value={formData.origin} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                    <input
                      type="text" name="destination" required placeholder={c.form.destination} value={formData.destination} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <select
                      name="cargoType" required value={formData.cargoType} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    >
                      <option value="">{c.form.cargoType}</option>
                      {c.form.cargoOptions.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                    </select>
                    <select
                      name="urgency" required value={formData.urgency} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    >
                      <option value="">{c.form.urgency}</option>
                      {c.form.urgencyOptions.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <textarea
                    name="message" rows={3} placeholder={c.form.message} value={formData.message} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-800 dark:text-white outline-none resize-none"
                  />
                  <button
                    type="submit" disabled={isSubmitting}
                    className="w-full bg-[#3ECDA3] hover:bg-[#35b892] text-white py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {c.form.submit}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AirTransportPage;
